'use client';

import React, { useEffect, useRef } from 'react';
import AppImage from '@/components/ui/AppImage';

interface PortfolioCategory {
  label: string;
  description: string;
  image: string | null;
  alt: string;
  span: string;
  isReal: boolean;
}

const portfolioCategories: PortfolioCategory[] = [
  {
    label: 'Cars',
    description: 'Detail shots, hero frames, and motion captures of builds worth remembering.',
    image: '/assets/images/IMG_3505-1772973384535.jpg',
    alt: 'Row of cars lined up at a car meet — navy BMW on the left, Nogaro Blue Audi RS4 in the center, with more cars behind',
    span: 'col-span-1 md:col-span-2 row-span-1 md:row-span-2',
    isReal: true,
  },
  {
    label: 'People',
    description: 'The culture lives in the faces. Candid portraits from the scene.',
    image: '/assets/images/IMG_2467-1772962399881.jpg',
    alt: 'Blonde woman in a black hoodie standing at a car meet during golden hour with cars visible in the background',
    span: 'col-span-1 row-span-1',
    isReal: true,
  },
  {
    label: 'Events',
    description: 'Full atmosphere coverage — the crowd, the smoke, the energy.',
    image: '/assets/images/IMG_3617-1772962097163.jpg',
    alt: 'Aerial view of a large outdoor car show with colorful cars including yellow NSX, red BMW E36, and Porsches surrounded by crowds of people',
    span: 'col-span-1 row-span-1',
    isReal: true,
  },
  {
    label: 'Video',
    description: 'Short-form cinematic edits for social and brand storytelling.',
    image: '/assets/images/_DSC4051-1772961925107.jpg',
    alt: 'Close-up of glowing red round taillights on a Nissan Skyline R34 against a dark background',
    span: 'col-span-1 md:col-span-2 row-span-1',
    isReal: true,
  },
];

export default function TerritorySection() {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const els = entry.target.querySelectorAll('.reveal-hidden');
            els.forEach((el, i) => {
              setTimeout(() => {
                el.classList.remove('reveal-hidden');
                el.classList.add('reveal-visible');
              }, i * 120);
            });
          }
        });
      },
      { threshold: 0.08 }
    );
    if (sectionRef?.current) observer?.observe(sectionRef?.current);
    return () => observer?.disconnect();
  }, []);

  return (
    <section id="portfolio" ref={sectionRef} className="py-28 md:py-36 border-t border-parchment/8">
      <div className="max-w-8xl mx-auto px-8 md:px-14">

        {/* Section label */}
        <div className="flex items-center gap-6 mb-20 reveal-hidden">
          <span className="text-gold text-[0.62rem] tracking-[0.28em] uppercase font-medium">
            03 — Portfolio
          </span>
          <div className="flex-1 h-px bg-parchment/8" />
        </div>

        {/* Pull quote */}
        <div className="mb-16 reveal-hidden">
          <blockquote className="pull-quote">
            <p className="font-serif text-parchment text-display-md italic font-light leading-[1.15] max-w-3xl">
              The cars are the subject. The culture is the story.
            </p>
          </blockquote>
        </div>

        {/* Portfolio bento grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 auto-rows-[280px] md:auto-rows-[240px]">
          {portfolioCategories?.map((cat, i) => (
            <div
              key={cat?.label}
              className={`${cat?.span} relative overflow-hidden group cursor-pointer reveal-hidden reveal-delay-${Math.min(i + 1, 4)}`}
            >
              {cat.isReal && cat.image ? (
                <>
                  <AppImage
                    src={cat.image}
                    alt={cat.alt}
                    fill
                    className="object-cover object-center transition-transform duration-700 ease-out group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-ink/40 group-hover:bg-ink/20 transition-colors duration-500" />
                  <div className="absolute inset-0 bg-gradient-to-t from-ink/90 via-transparent to-transparent" />
                </>
              ) : (
                <>
                  <div className="absolute inset-0 bg-gradient-to-br from-ink via-[#0d0d0d] to-[#1a1a1a]" />
                  <div className="absolute inset-0 bg-gradient-to-t from-ink/80 via-transparent to-transparent" />
                  <div
                    className="absolute inset-0 opacity-[0.04]"
                    style={{
                      backgroundImage:
                        'linear-gradient(rgba(212,175,55,0.4) 1px, transparent 1px), linear-gradient(90deg, rgba(212,175,55,0.4) 1px, transparent 1px)',
                      backgroundSize: '40px 40px',
                    }}
                  />
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-center">
                    <span className="text-parchment/20 text-[0.58rem] tracking-[0.3em] uppercase font-medium">
                      Coming Soon
                    </span>
                  </div>
                </>
              )}

              {/* Label */}
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <p className="text-gold text-[0.62rem] tracking-[0.22em] uppercase font-medium mb-1.5">
                  {cat?.label}
                </p>
                <p className="text-parchment/60 text-xs font-light leading-relaxed tracking-wide max-w-xs">
                  {cat?.description}
                </p>
              </div>

              {/* Corner accent */}
              <div className="absolute top-4 right-4 w-5 h-5 border-t border-r border-gold/30 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            </div>
          ))}
        </div>

        {/* Footnote */}
        <p className="text-parchment/25 text-[0.65rem] tracking-wide mt-8 font-light reveal-hidden">
          Portfolio available on request. Full galleries shared after inquiry.
        </p>

      </div>
    </section>
  );
}